import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { getMacroColors } from "../../helpers/macroColors";

export type MealSegment = {
  mealType: number;
  value: number;
  label: string;
};

type Props = {
  allMealsData: MealSegment[];
  currentMealType: number;
  dailyTotal: number;
  /** "kcal" or "gr". */
  label: string;
  nutrientType: "calories" | "carbs" | "protein" | "fat";
  /** 120 for calories, 90 for the macros. */
  size: number;
  /** Macro name above the ring; empty for calories. */
  title: string;
};

const GAP_DEGREES = 8;

/**
 * The app's `MultiMealSegmentedProgress`: one arc per meal, sized by that
 * meal's share of the day, with the selected meal coloured and the rest grey.
 * The centre reads "meal value / daily total".
 */
const MultiMealSegmentedProgress = ({
  allMealsData,
  currentMealType,
  dailyTotal,
  label,
  nutrientType,
  size,
  title,
}: Props) => {
  const theme: Theme = useTheme();
  const strokeWidth = size > 100 ? 6 : 4;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const isLarge = size > 100;

  const highlight = {
    calories: theme.colors_ds.PRIMARY_GRAPH_MAIN,
    ...getMacroColors(theme),
  }[nutrientType];

  const total = allMealsData.reduce((sum, meal) => sum + meal.value, 0);
  const availableDegrees = 360 - allMealsData.length * GAP_DEGREES;

  let cursor = -90;
  const segments = allMealsData.map((meal) => {
    const proportion = total > 0 ? meal.value / total : 0;
    const degrees = proportion * availableDegrees;
    const startAngle = cursor;
    cursor += degrees + GAP_DEGREES;
    return {
      ...meal,
      degrees,
      startAngle,
      endAngle: startAngle + degrees,
      isCurrent: meal.mealType === currentMealType,
    };
  });
  const currentValue =
    segments.find((segment) => segment.isCurrent)?.value ?? 0;

  const arcPath = (startAngle: number, endAngle: number) => {
    const start = (startAngle * Math.PI) / 180;
    const end = (endAngle * Math.PI) / 180;
    const x1 = center + radius * Math.cos(start);
    const y1 = center + radius * Math.sin(start);
    const x2 = center + radius * Math.cos(end);
    const y2 = center + radius * Math.sin(end);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  return (
    <Box sx={styles.container}>
      {title && (
        <Box sx={styles.titleRow}>
          <Box sx={styles.dot(highlight)} />
          <Box sx={styles.titleText(theme)}>{title}</Box>
        </Box>
      )}
      <Box sx={styles.ring(size)}>
        <svg width={size} height={size} style={{ position: "absolute" }}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={theme.colorTokens.N[10]}
            strokeWidth={strokeWidth}
            fill="none"
          />
          {segments
            .filter((segment) => segment.degrees > 0)
            .map((segment) => (
              <path
                key={segment.mealType}
                d={arcPath(segment.startAngle, segment.endAngle)}
                stroke={segment.isCurrent ? highlight : theme.colorTokens.N[40]}
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
              />
            ))}
        </svg>
        <Box sx={styles.centerText}>
          <Box sx={styles.value(theme, isLarge)}>
            {currentValue.toFixed(0)}
            <Box component="span" sx={styles.separator(theme, isLarge)}>
              {" / "}
            </Box>
          </Box>
          <Box sx={styles.total(theme, isLarge)}>
            {dailyTotal.toFixed(0)} {label}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MultiMealSegmentedProgress;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "12px",
  },
  dot: (color: string) => ({
    width: "8px",
    height: "8px",
    borderRadius: "4px",
    backgroundColor: color,
  }),
  titleText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  ring: (size: number) => ({
    position: "relative" as "relative",
    width: `${size}px`,
    height: `${size}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  centerText: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    zIndex: 1,
  },
  value: (theme: Theme, isLarge: boolean) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: isLarge ? "22px" : "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  separator: (theme: Theme, isLarge: boolean) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: isLarge ? "14px" : "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  total: (theme: Theme, isLarge: boolean) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: isLarge ? "14px" : "12px",
    lineHeight: isLarge ? "22px" : "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};
